import { useState } from 'react';
import { authClient } from '@/lib/auth-client';
import { LinkLogo } from '@/components/nav/link-logo';
import { createFileRoute, Link, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';
import { toast } from 'sonner';
import { useAppForm } from '@/hooks/use-app-form';
import { getSafeAuthRedirectURL } from '@/lib/auth-redirect';

const forgotPasswordSchema = z.object({
  email: z.string({ message: 'Please enter your email address.' }).email({
    message: 'Please enter a valid email address.',
  }),
});

type ForgotPasswordFormValues = z.infer<typeof forgotPasswordSchema>;

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPassword,
  beforeLoad: async ({ context }) => {
    if (context.isAuthenticated()) {
      throw redirect({ to: '/' });
    }
  },
});

function ForgotPassword() {
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const form = useAppForm({
    defaultValues: { email: '' } as ForgotPasswordFormValues,
    validators: { onSubmit: forgotPasswordSchema },
    onSubmit: async ({ value }: { value: ForgotPasswordFormValues }) => {
      setIsLoading(true);
      const { error: reqError } = await authClient.requestPasswordReset({
        email: value.email,
        redirectTo: getSafeAuthRedirectURL('/reset-password'),
      });
      setIsLoading(false);
      if (reqError) {
        toast.error('Error', {
          description: reqError.message || 'Failed to send reset email.',
        });
      } else {
        setEmailSent(true);
      }
    },
  });

  return (
    <section className="py-12 sm:py-16 lg:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-sm">
          <div className="flex flex-col items-center">
            <LinkLogo />
            <h1 className="mt-12 text-3xl font-bold">Reset Your Password</h1>
            <p className="text-muted-foreground mt-4 font-medium">
              Enter your email and we&apos;ll send you a reset link.
            </p>
          </div>

          <div className="mt-12">
            {emailSent ? (
              <div className="space-y-4 text-center">
                <Alert
                  title="Check Your Email"
                  subTitle="We've sent you a link to reset your password."
                  variant="success"
                />
                <Button
                  variant="outline"
                  onClick={() => setEmailSent(false)}
                  className="w-full"
                >
                  Send Another Link
                </Button>
              </div>
            ) : (
              <form
                className="space-y-5"
                onSubmit={(e) => {
                  e.preventDefault();
                  form.handleSubmit();
                }}
              >
                <form.AppForm>
                  <form.AppField name="email">
                    {(field) => (
                      <field.Input
                        label="Email"
                        className="h-10"
                        placeholder="Enter Your Email Address..."
                      />
                    )}
                  </form.AppField>
                </form.AppForm>
                <Button
                  className="w-full"
                  size="lg"
                  type="submit"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
              </form>
            )}
          </div>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground font-medium">
              Remember your password?{' '}
              <Link to="/sign-in">
                <Button className="px-1" variant="link">
                  {' '}
                  Sign In{' '}
                </Button>
              </Link>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
